import React, { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  // Configuração das 5 línguas
  const languages = [
    { code: 'pt', label: 'PT' },
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'nl', label: 'NL' },
    { code: 'fr', label: 'FR' }
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks = [
    { key: "home", href: "#home" },
    { key: "about", href: "#about" },
    { key: "accommodations", href: "#accommodations" },
    { key: "location", href: "#location" },
    { key: "gallery", href: "#gallery" },
    { key: "contact", href: "#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-lg bg-[#FDFCF8]/90 border-b border-stone-200/60 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-20 py-2">
        {/* Logo Section - Mantido Original */}
        <a href="#home" className="flex items-center group" data-testid="navbar-logo">
          <div className="h-14 w-14 md:h-16 md:w-16 rounded-full overflow-hidden bg-transparent flex items-center justify-center transition-transform duration-300 group-hover:scale-105 border border-stone-100/20 shadow-sm">
            <img 
              src="/cb1ce97b-ee70-4a21-a6cd-ee93462e9112.jpg" 
              alt="Quinta das Cantigas Logo" 
              className="h-full w-full object-cover mix-blend-multiply brightness-105 contrast-110" 
            />
          </div>
        </a>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className={`text-sm font-medium transition-colors duration-200 font-manrope ${
                scrolled ? "text-[#6B7280] hover:text-[#3A4D39]" : "text-white/90 hover:text-white"
              }`}
            >
              {t(`nav.${link.key}`)}
            </a>
          ))}
        </div>

        {/* Desktop Actions - Agora com as 5 Línguas */}
        <div className="hidden lg:flex items-center gap-4">
          <div className={`flex items-center gap-1 p-1 rounded-full border transition-colors ${
            scrolled ? "bg-stone-200/40 border-stone-200" : "bg-white/10 border-white/20"
          }`}>
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`text-[11px] font-bold w-8 h-8 rounded-full transition-all duration-300 ${
                  lang === l.code 
                    ? "bg-[#3A4D39] text-white shadow-sm scale-110" 
                    : scrolled ? "text-[#6B7280] hover:text-[#3A4D39]" : "text-white/70 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href="https://book.yourholiday.pt/en/search-availability/"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-5 py-2.5 bg-[#3A4D39] text-white text-sm font-medium hover:bg-[#2C3E2B] transition-all duration-300 hover:scale-[1.02] shadow-md font-manrope"
          >
            {t("nav.book")}
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-2 rounded-lg transition-colors ${
            scrolled ? "text-[#1F2937] hover:bg-stone-100" : "text-white"
          }`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu - Adaptado para as 5 línguas */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#FDFCF8] border-t border-stone-100 px-5 py-6 flex flex-col gap-4 shadow-xl animate-in fade-in slide-in-from-top-5">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-lg font-semibold text-[#1F2937] hover:text-[#3A4D39] py-2 border-b border-stone-50 font-manrope"
            >
              {t(`nav.${link.key}`)}
            </a>
          ))}
          
          {/* Seletor de Línguas Mobile */}
          <div className="grid grid-cols-5 gap-2 pt-4">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setMobileOpen(false); }}
                className={`py-3 text-xs font-bold rounded-xl border transition-all ${
                  lang === l.code 
                    ? "bg-[#3A4D39] text-white border-[#3A4D39] shadow-inner" 
                    : "bg-white border-stone-200 text-[#6B7280]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href="https://book.yourholiday.pt/en/search-availability/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-center rounded-xl py-4 bg-[#3A4D39] text-white font-bold shadow-lg mt-2"
          >
            {t("nav.book")}
          </a>
        </div>
      )}
    </nav>
  );
}
